import React, { useState, useContext } from "react";
import axios from "axios";
import { FiCheck, FiAlertCircle, FiZap, FiArrowRight } from "react-icons/fi";
import { BsBoxSeam, BsStars } from "react-icons/bs";
import { AuthContext } from "../context/AuthContext";

/* ═══════════════════════════════════════════
   STYLES — Montserrat · Navy + Amber
═══════════════════════════════════════════ */
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');

  .rp-root {
    font-family: 'Montserrat', sans-serif;
    background: #f0f4f8; min-height: 100%;
  }
  .rp-root *, .rp-root *::before, .rp-root *::after { box-sizing: border-box; }
  .rp-root ::-webkit-scrollbar { width: 4px; }
  .rp-root ::-webkit-scrollbar-track { background: transparent; }
  .rp-root ::-webkit-scrollbar-thumb { background: #94a3b8; border-radius: 99px; }

  .rp-page {
    max-width: 1100px; margin: 0 auto;
    padding: 24px 20px;
    display: flex; flex-direction: column; gap: 20px;
  }
  @media (max-width: 600px) { .rp-page { padding: 14px 12px; gap: 14px; } }

  /* ══════════════════════════════
     HERO
  ══════════════════════════════ */
  .rp-hero {
    background: linear-gradient(135deg, #0a1628 0%, #0f2040 55%, #162a52 100%);
    border-radius: 22px; padding: 28px;
    display: flex; align-items: center; gap: 20px;
    position: relative; overflow: hidden; flex-wrap: wrap;
  }
  .rp-hero::before {
    content: ''; position: absolute; top: -80px; right: -60px;
    width: 260px; height: 260px; border-radius: 50%;
    background: radial-gradient(circle, rgba(251,191,36,.16) 0%, transparent 70%);
    pointer-events: none;
  }
  .rp-hero::after {
    content: ''; position: absolute; bottom: -40px; left: 25%;
    width: 180px; height: 180px; border-radius: 50%;
    background: radial-gradient(circle, rgba(99,102,241,.08) 0%, transparent 70%);
    pointer-events: none;
  }
  .rp-hero-icon {
    width: 64px; height: 64px; border-radius: 18px; flex-shrink: 0;
    background: linear-gradient(135deg, #fbbf24, #f59e0b);
    display: flex; align-items: center; justify-content: center;
    box-shadow: 0 0 0 5px rgba(251,191,36,.15), 0 8px 24px rgba(251,191,36,.2);
    position: relative; z-index: 1; font-size: 26px; color: #0f172a;
  }
  .rp-hero-text { position: relative; z-index: 1; }
  .rp-hero-badge {
    display: inline-flex; align-items: center; gap: 6px;
    background: rgba(251,191,36,.15); border: 1px solid rgba(251,191,36,.3);
    border-radius: 99px; padding: 4px 12px;
    font-size: 11px; font-weight: 700; color: #fcd34d;
    letter-spacing: .07em; text-transform: uppercase; margin-bottom: 8px;
  }
  .rp-badge-dot {
    width: 6px; height: 6px; border-radius: 50%; background: #fbbf24;
    animation: rp-pulse 2s infinite;
  }
  @keyframes rp-pulse { 0%,100%{opacity:1} 50%{opacity:.35} }
  .rp-hero-title {
    font-size: clamp(20px, 4vw, 26px); font-weight: 900;
    color: #fff; line-height: 1.2; margin-bottom: 5px;
  }
  .rp-hero-sub { font-size: 13px; font-weight: 500; color: #64748b; }

  /* ══════════════════════════════
     ALERTS
  ══════════════════════════════ */
  .rp-alert {
    display: flex; align-items: flex-start; gap: 10px;
    padding: 13px 16px; border-radius: 13px;
    font-size: 13px; font-weight: 600;
  }
  .rp-alert.error   { background: #fef2f2; border: 1px solid #fecaca; color: #b91c1c; }
  .rp-alert.success { background: #f0fdf4; border: 1px solid #bbf7d0; color: #15803d; }
  .rp-alert svg { flex-shrink: 0; margin-top: 1px; }

  /* ══════════════════════════════
     INFO BANNER
  ══════════════════════════════ */
  .rp-info-banner {
    display: flex; align-items: flex-start; gap: 10px;
    padding: 13px 16px; border-radius: 13px;
    background: linear-gradient(135deg, #eff6ff, #dbeafe);
    border: 1px solid #bfdbfe;
    font-size: 12.5px; font-weight: 600; color: #1d4ed8;
  }
  .rp-info-banner svg { flex-shrink: 0; margin-top: 1px; color: #2563eb; }

  /* ══════════════════════════════
     PARTNER CARDS GRID
  ══════════════════════════════ */
  .rp-cards-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }
  @media (max-width: 860px) { .rp-cards-grid { grid-template-columns: 1fr; gap: 14px; } }

  /* ── Plan card ── */
  .rp-plan-card {
    border-radius: 20px; padding: 24px;
    border: 2px solid #e2e8f0; background: #fff;
    cursor: pointer; position: relative; overflow: hidden;
    transition: all .22s;
    display: flex; flex-direction: column; gap: 18px;
  }
  .rp-plan-card::before {
    content: ''; position: absolute; top: 0; left: 0; right: 0;
    height: 4px; border-radius: 20px 20px 0 0;
    transition: opacity .22s;
  }
  .rp-plan-card.lbbp::before { background: linear-gradient(90deg, #3b82f6, #60a5fa); }
  .rp-plan-card.puap::before { background: linear-gradient(90deg, #10b981, #34d399); }
  .rp-plan-card.both::before { background: linear-gradient(90deg, #fbbf24, #f59e0b, #fbbf24); background-size: 200%; animation: rp-gold-shimmer 2s linear infinite; }
  @keyframes rp-gold-shimmer { 0%{background-position:0%} 100%{background-position:200%} }

  .rp-plan-card:not(.selected) { opacity: .85; }
  .rp-plan-card:not(.selected):hover {
    border-color: #cbd5e1; opacity: 1;
    transform: translateY(-2px); box-shadow: 0 6px 20px rgba(0,0,0,.08);
  }
  .rp-plan-card.selected {
    opacity: 1; border-color: transparent;
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(0,0,0,.12);
  }
  .rp-plan-card.lbbp.selected { border-color: #3b82f6; box-shadow: 0 12px 32px rgba(59,130,246,.18); }
  .rp-plan-card.puap.selected { border-color: #10b981; box-shadow: 0 12px 32px rgba(16,185,129,.18); }
  .rp-plan-card.both.selected { border-color: #fbbf24; box-shadow: 0 12px 32px rgba(251,191,36,.22); }

  /* recommended ribbon */
  .rp-ribbon {
    position: absolute; top: 16px; right: -8px;
    background: linear-gradient(135deg, #fbbf24, #f59e0b);
    color: #0f172a; font-size: 10px; font-weight: 900;
    padding: 3px 16px 3px 10px; border-radius: 99px 0 0 99px;
    letter-spacing: .06em; text-transform: uppercase;
    box-shadow: 0 2px 8px rgba(251,191,36,.35);
  }

  /* card header */
  .rp-card-header { display: flex; align-items: flex-start; gap: 14px; }
  .rp-card-icon {
    width: 48px; height: 48px; border-radius: 14px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center; font-size: 20px;
  }
  .rp-plan-card.lbbp .rp-card-icon { background: linear-gradient(135deg,#dbeafe,#bfdbfe); color: #2563eb; }
  .rp-plan-card.puap .rp-card-icon { background: linear-gradient(135deg,#dcfce7,#bbf7d0); color: #059669; }
  .rp-plan-card.both .rp-card-icon { background: linear-gradient(135deg,#fef3c7,#fde68a); color: #d97706; }

  .rp-card-title { font-size: 15px; font-weight: 900; color: #0f172a; margin-bottom: 4px; }
  .rp-card-desc  { font-size: 12px; font-weight: 600; color: #64748b; line-height: 1.5; }

  /* selected badge */
  .rp-selected-badge {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 4px 10px; border-radius: 99px;
    font-size: 11px; font-weight: 800; margin-top: 6px;
  }
  .rp-plan-card.lbbp .rp-selected-badge { background: #dbeafe; color: #1d4ed8; }
  .rp-plan-card.puap .rp-selected-badge { background: #dcfce7; color: #15803d; }
  .rp-plan-card.both .rp-selected-badge { background: linear-gradient(135deg,#fef3c7,#fde68a); color: #92400e; }

  /* benefits list */
  .rp-benefits { display: flex; flex-direction: column; gap: 7px; }
  .rp-benefit-item {
    display: flex; align-items: center; gap: 8px;
    font-size: 12.5px; font-weight: 600; color: #334155;
  }
  .rp-benefit-dot {
    width: 18px; height: 18px; border-radius: 50%; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    font-size: 10px; color: #fff;
  }
  .rp-plan-card.lbbp .rp-benefit-dot { background: #3b82f6; }
  .rp-plan-card.puap .rp-benefit-dot { background: #10b981; }
  .rp-plan-card.both .rp-benefit-dot { background: linear-gradient(135deg,#fbbf24,#f59e0b); color: #0f172a; }

  /* divider */
  .rp-card-divider { height: 1px; background: #f1f5f9; }

  /* ══════════════════════════════
     SUMMARY CARD
  ══════════════════════════════ */
  .rp-summary {
    background: #fff; border-radius: 20px; padding: 22px;
    border: 1px solid #e2e8f0; box-shadow: 0 1px 4px rgba(0,0,0,.05);
    display: flex; align-items: center; justify-content: space-between;
    gap: 16px; flex-wrap: wrap;
  }
  .rp-summary-left { display: flex; align-items: center; gap: 14px; }
  .rp-summary-icon {
    width: 46px; height: 46px; border-radius: 14px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center; font-size: 20px;
    background: linear-gradient(135deg, #0f2040, #162a52); color: #fbbf24;
  }
  .rp-summary-label { font-size: 11px; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: .07em; }
  .rp-summary-val { font-size: 17px; font-weight: 900; color: #0f172a; margin-top: 2px; }

  /* ══════════════════════════════
     SUBMIT BUTTON
  ══════════════════════════════ */
  .rp-submit-btn {
    width: 100%; padding: 15px;
    border: none; border-radius: 14px; cursor: pointer;
    font-family: 'Montserrat', sans-serif; font-size: 14px; font-weight: 900;
    display: flex; align-items: center; justify-content: center; gap: 9px;
    transition: all .22s; letter-spacing: .04em;
  }
  .rp-submit-btn.ready {
    background: linear-gradient(135deg, #0f2040, #162a52);
    color: #fbbf24; box-shadow: 0 4px 18px rgba(15,32,64,.25);
  }
  .rp-submit-btn.ready:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(15,32,64,.35); }
  .rp-submit-btn.loading { background: #e2e8f0; color: #94a3b8; cursor: not-allowed; }
  .rp-submit-btn.loading svg { animation: rp-spin .7s linear infinite; }
  @keyframes rp-spin { to { transform: rotate(360deg); } }

  /* ══════════════════════════════
     NOTICE
  ══════════════════════════════ */
  .rp-notice {
    display: flex; align-items: flex-start; gap: 10px;
    padding: 14px 16px; border-radius: 13px;
    background: linear-gradient(135deg, #fffbeb, #fef9c3);
    border: 1px solid #fde68a;
    font-size: 12.5px; font-weight: 600; color: #92400e; line-height: 1.6;
  }
  .rp-notice svg { flex-shrink: 0; margin-top: 1px; color: #d97706; }

  /* ══════════════════════════════
     SUCCESS STATE
  ══════════════════════════════ */
  .rp-success-wrap {
    text-align: center; padding: 40px 20px;
  }
  .rp-success-circle {
    width: 80px; height: 80px; border-radius: 50%;
    background: linear-gradient(135deg, #10b981, #059669);
    display: flex; align-items: center; justify-content: center;
    margin: 0 auto 20px;
    box-shadow: 0 0 0 10px rgba(16,185,129,.1);
  }
  .rp-success-title { font-size: 20px; font-weight: 900; color: #0f172a; margin-bottom: 8px; }
  .rp-success-sub   { font-size: 13px; font-weight: 600; color: #64748b; }

  /* ══════════════════════════════
     MAIN CARD WRAPPER
  ══════════════════════════════ */
  .rp-card {
    background: #fff; border-radius: 20px; padding: 28px;
    border: 1px solid #e2e8f0; box-shadow: 0 1px 4px rgba(0,0,0,.05);
    display: flex; flex-direction: column; gap: 22px;
  }
  @media (max-width: 600px) { .rp-card { padding: 18px 16px; gap: 16px; } }
`;

/* ═══════════════════════════════════════════
   PARTNER PLAN DATA
═══════════════════════════════════════════ */
const PLANS = [
  {
    type: "lbbp",
    title: "LBBP Partner",
    desc: "Investment-based income model with weekly EMI payouts",
    icon: <BsBoxSeam size={22}/>,
    benefits: [
      "5% direct level income",
      "Weekly EMI payouts",
      "3-level commission structure",
      "Bank & UPI withdrawals",
    ],
  },
  {
    type: "puap",
    title: "PU&AP Partner",
    desc: "Product sales, commissions and generation bonuses",
    icon: <BsBoxSeam size={22}/>,
    benefits: [
      "Product commissions up to 10%",
      "Matrix income structure",
      "Generation bonuses",
      "Product rewards & cashbacks",
    ],
  },
  {
    type: "both",
    title: "Dual Partner",
    desc: "Maximum earning potential with all programs combined",
    icon: <BsStars size={22}/>,
    recommended: true,
    benefits: [
      "All LBBP benefits included",
      "All PU&AP benefits included",
      "Multiple income streams",
      "Highest growth potential",
    ],
  },
];

const LABEL_MAP = { lbbp: "LBBP Partnership", puap: "PU&AP Partnership", both: "Dual Partnership" };
const ICON_MAP  = {
  lbbp: <BsBoxSeam size={20}/>,
  puap: <BsBoxSeam size={20}/>,
  both: <BsStars size={20}/>,
};

/* ═══════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════ */
const RegisterPartner = () => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");
  const [success, setSuccess]   = useState("");
  const [selected, setSelected] = useState("both");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); setSuccess(""); setLoading(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/register-partner/${user.id}`,
        { partnerType: selected }
      );
      setSuccess("Partnership selected successfully!");
    } catch (err) {
      setError(err.response?.data?.message || "Partner selection failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rp-root">
      <style>{styles}</style>
      <div className="rp-page">

        {/* ── Hero ── */}
        <div className="rp-hero">
          <div className="rp-hero-icon"><BsStars/></div>
          <div className="rp-hero-text">
            <div className="rp-hero-badge"><div className="rp-badge-dot"/>Onboarding</div>
            <div className="rp-hero-title">Select Your Partnership</div>
            <div className="rp-hero-sub">Choose the program that fits your goals</div>
          </div>
        </div>

        {/* ── Main card ── */}
        <div className="rp-card">

          {/* Alerts */}
          {error   && <div className="rp-alert error">  <FiAlertCircle size={15}/>{error}  </div>}
          {success && <div className="rp-alert success"><FiCheck size={15}/>{success}</div>}

          {success ? (
            /* ── Success state ── */
            <div className="rp-success-wrap">
              <div className="rp-success-circle"><FiCheck size={34} color="#fff"/></div>
              <div className="rp-success-title">Partnership Activated!</div>
              <div className="rp-success-sub">
                Your <strong>{LABEL_MAP[selected]}</strong> has been registered successfully.<br/>
                You can now access all features of your selected program.
              </div>
            </div>
          ) : (
            <>
              {/* Info banner */}
              <div className="rp-info-banner">
                <FiZap size={15}/>
                Select your preferred partnership program. You can always upgrade later from your profile settings.
              </div>

              <form onSubmit={handleSubmit} style={{ display:"flex", flexDirection:"column", gap:22 }}>

                {/* ── Plan cards ── */}
                <div className="rp-cards-grid">
                  {PLANS.map(plan => (
                    <div
                      key={plan.type}
                      className={`rp-plan-card ${plan.type} ${selected === plan.type ? "selected" : ""}`}
                      onClick={() => setSelected(plan.type)}
                    >
                      {plan.recommended && <div className="rp-ribbon">⭐ Recommended</div>}

                      {/* Header */}
                      <div className="rp-card-header">
                        <div className="rp-card-icon">{plan.icon}</div>
                        <div>
                          <div className="rp-card-title">{plan.title}</div>
                          <div className="rp-card-desc">{plan.desc}</div>
                          {selected === plan.type && (
                            <div className="rp-selected-badge">
                              <FiCheck size={10}/> Selected
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="rp-card-divider"/>

                      {/* Benefits */}
                      <div className="rp-benefits">
                        {plan.benefits.map((b, i) => (
                          <div className="rp-benefit-item" key={i}>
                            <div className="rp-benefit-dot"><FiCheck size={10}/></div>
                            {b}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* ── Selection summary ── */}
                <div className="rp-summary">
                  <div className="rp-summary-left">
                    <div className="rp-summary-icon">{ICON_MAP[selected]}</div>
                    <div>
                      <div className="rp-summary-label">Selected Program</div>
                      <div className="rp-summary-val">{LABEL_MAP[selected]}</div>
                    </div>
                  </div>
                  <div style={{ fontSize:12, fontWeight:600, color:"#94a3b8", maxWidth:220, textAlign:"right" }}>
                    Click any card above to change your selection
                  </div>
                </div>

                {/* ── Submit ── */}
                <button
                  type="submit"
                  disabled={loading}
                  className={`rp-submit-btn ${loading ? "loading" : "ready"}`}
                >
                  <FiArrowRight size={16}/>
                  {loading ? "Processing…" : `Confirm ${LABEL_MAP[selected]}`}
                </button>

                {/* ── Notice ── */}
                <div className="rp-notice">
                  <FiZap size={15}/>
                  <span>
                    <strong>Note:</strong> You can upgrade your partnership at any time from your profile settings.
                    Choose what works best for your current situation and goals.
                  </span>
                </div>

              </form>
            </>
          )}
        </div>

      </div>
    </div>
  );
};

export default RegisterPartner;